import { useState, useEffect, useRef } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import BottomNav from "@/components/BottomNav";
import Header from "@/components/Header";
import { useAuth } from "@/App";
import { Building2, Phone, Mail, MapPin, Save, Building, ArrowRight, Key, Plus, Trash2, Copy, Power, Eye, EyeOff, Globe, ExternalLink, Brain, RefreshCw, FileJson, FileText, CheckCircle, Shield, AlertTriangle, Calendar, Clock, Zap, ImageIcon, Upload, Pencil, X, Book } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const FRONTEND_URL = window.location.origin;

export default function SettingsPage() {
  const { organization, organizations, isAdmin, fetchUser, switchOrganization } = useAuth();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
    address: "",
  });

  // Public booking state
  const [publicSlug, setPublicSlug] = useState("");
  const [publicBookingEnabled, setPublicBookingEnabled] = useState(true);
  const [savingPublic, setSavingPublic] = useState(false);

  // API Keys state
  const [apiKeys, setApiKeys] = useState([]);
  const [loadingKeys, setLoadingKeys] = useState(false);
  const [showCreateKey, setShowCreateKey] = useState(false);
  const [newKeyName, setNewKeyName] = useState("");
  const [creatingKey, setCreatingKey] = useState(false);
  const [newlyCreatedKey, setNewlyCreatedKey] = useState(null);
  const [showKey, setShowKey] = useState(false);

  // Knowledge Base IA state
  const [kbSettings, setKbSettings] = useState(null);
  const [loadingKb, setLoadingKb] = useState(false);
  const [showKbPolicies, setShowKbPolicies] = useState(false);
  const [regeneratingToken, setRegeneratingToken] = useState(false);
  const [savingPolicies, setSavingPolicies] = useState(false);
  const [kbPoliciesForm, setKbPoliciesForm] = useState({
    company_description: "",
    service_area: [],
    delivery_conditions: "",
    cancellation_policy: "",
    deposit_required: "",
    safety_rules: "",
  });
  const [serviceAreaInput, setServiceAreaInput] = useState("");

  // Priority Instructions state
  const [showPriorityInstructions, setShowPriorityInstructions] = useState(false);
  const [savingPriority, setSavingPriority] = useState(false);
  const [priorityForm, setPriorityForm] = useState({
    title: "",
    content: "",
    start_date: "",
    end_date: "",
    is_absolute: false,
    is_active: false,
  });

  // Catalog Images for AI state
  const [catalogImages, setCatalogImages] = useState([]);
  const [loadingCatalogImages, setLoadingCatalogImages] = useState(false);
  const [showAddCatalogImage, setShowAddCatalogImage] = useState(false);
  const [uploadingCatalogImage, setUploadingCatalogImage] = useState(false);
  const [newCatalogImage, setNewCatalogImage] = useState({ image_url: "", description: "" });
  const [editingCatalogImage, setEditingCatalogImage] = useState(null);
  const catalogImageInputRef = useRef(null);

  // Time Slots Configuration state
  const [timeSlots, setTimeSlots] = useState(null);
  const [loadingTimeSlots, setLoadingTimeSlots] = useState(false);
  const [savingTimeSlots, setSavingTimeSlots] = useState(false);
  const [showTimeSlots, setShowTimeSlots] = useState(false);

  useEffect(() => {
    if (organization) {
      setFormData({
        name: organization.name || "",
        phone: organization.phone || "",
        email: organization.email || "",
        address: organization.address || "",
      });
      setPublicSlug(organization.public_slug || "");
      setPublicBookingEnabled(organization.public_booking_enabled !== false);
    }
  }, [organization]);

  useEffect(() => {
    if (isAdmin) {
      fetchApiKeys();
      fetchKbSettings();
      fetchCatalogImages();
      fetchTimeSlots();
    }
  }, [isAdmin]);

  const fetchTimeSlots = async () => {
    setLoadingTimeSlots(true);
    try {
      const response = await fetch(`${API}/settings/time-slots`, { credentials: "include" });
      if (response.ok) {
        const data = await response.json();
        setTimeSlots(data.time_slots);
      }
    } catch (error) {
    } finally {
      setLoadingTimeSlots(false);
    }
  };

  const handleSaveTimeSlots = async () => {
    setSavingTimeSlots(true);
    try {
      const response = await fetch(`${API}/settings/time-slots`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ time_slots: timeSlots }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail);
      }

      toast.success("Créneaux horaires mis à jour");
      setShowTimeSlots(false);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSavingTimeSlots(false);
    }
  };

  const updateTimeSlot = (slotId, field, value) => {
    setTimeSlots(prev => ({
      ...prev,
      [slotId]: {
        ...prev[slotId],
        [field]: value
      }
    }));
  };

  const fetchKbSettings = async () => {
    setLoadingKb(true);
    try {
      const response = await fetch(`${API}/kb-settings`, { credentials: "include" });
      if (response.ok) {
        const data = await response.json();
        setKbSettings(data);
        setKbPoliciesForm({
          company_description: data.company_description || "",
          service_area: data.service_area || [],
          delivery_conditions: data.delivery_conditions || "",
          cancellation_policy: data.cancellation_policy || "",
          deposit_required: data.deposit_required || "",
          safety_rules: data.safety_rules || "",
        });
        // Load priority instructions
        if (data.priority_instructions) {
          const pi = data.priority_instructions;
          setPriorityForm({
            title: pi.title || "",
            content: pi.content || "",
            start_date: pi.start_date ? pi.start_date.slice(0, 10) : "",
            end_date: pi.end_date ? pi.end_date.slice(0, 10) : "",
            is_absolute: pi.is_absolute || false,
            is_active: pi.is_active || false,
          });
        }
      }
    } catch (error) {
    } finally {
      setLoadingKb(false);
    }
  };

  const fetchApiKeys = async () => {
    setLoadingKeys(true);
    try {
      const response = await fetch(`${API}/api-keys`, { credentials: "include" });
      if (response.ok) {
        const data = await response.json();
        setApiKeys(data);
      }
    } catch (error) {
    } finally {
      setLoadingKeys(false);
    }
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API}/organizations/current`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail);
      }

      await fetchUser();
      toast.success("Paramètres enregistrés");
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSavePublicBooking = async () => {
    setSavingPublic(true);
    try {
      const response = await fetch(`${API}/organizations/current/public-booking`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          public_slug: publicSlug || null,
          public_booking_enabled: publicBookingEnabled,
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail);
      }

      await fetchUser();
      toast.success("Page de réservation mise à jour");
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSavingPublic(false);
    }
  };

  const handleSwitchOrg = async (orgId) => {
    const success = await switchOrganization(orgId);
    if (success) {
      toast.success("Organisation changée");
    }
  };

  const handleCreateApiKey = async () => {
    if (!newKeyName.trim()) {
      toast.error("Veuillez entrer un nom pour la clé");
      return;
    }

    setCreatingKey(true);
    try {
      const response = await fetch(`${API}/api-keys`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ name: newKeyName }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail);
      }

      const data = await response.json();
      setNewlyCreatedKey(data);
      setNewKeyName("");
      fetchApiKeys();
      toast.success("Clé API créée");
    } catch (error) {
      toast.error(error.message);
    } finally {
      setCreatingKey(false);
    }
  };

  const handleDeleteApiKey = async (keyId) => {
    if (!window.confirm("Supprimer cette clé API ?")) return;

    try {
      const response = await fetch(`${API}/api-keys/${keyId}`, {
        method: "DELETE",
        credentials: "include",
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail);
      }

      fetchApiKeys();
      toast.success("Clé API supprimée");
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleToggleApiKey = async (keyId) => {
    try {
      const response = await fetch(`${API}/api-keys/${keyId}/toggle`, {
        method: "PUT",
        credentials: "include",
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail);
      }

      fetchApiKeys();
      toast.success("Statut mis à jour");
    } catch (error) {
      toast.error(error.message);
    }
  };

  const copyToClipboard = async (text) => {
    try {
      // Try modern Clipboard API first
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
        toast.success("Copié dans le presse-papiers");
        return;
      }
      
      // Fallback for restricted environments
      const textArea = document.createElement("textarea");
      textArea.value = text;
      textArea.style.position = "fixed";
      textArea.style.left = "-999999px";
      textArea.style.top = "-999999px";
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      
      const successful = document.execCommand("copy");
      document.body.removeChild(textArea);
      
      if (successful) {
        toast.success("Copié dans le presse-papiers");
      } else {
        throw new Error("Copy failed");
      }
    } catch (err) {
      // Last resort: show the text in an alert for manual copy
      toast.error("Copie automatique bloquée. URL affichée ci-dessous.");
      prompt("Copiez cette URL manuellement :", text);
    }
  };

  const handleRegenerateKbToken = async () => {
    if (!window.confirm("Régénérer le token ? L'ancien token ne fonctionnera plus.")) return;
    
    setRegeneratingToken(true);
    try {
      const response = await fetch(`${API}/kb-settings/regenerate-token`, {
        method: "POST",
        credentials: "include",
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail);
      }
      
      const data = await response.json();
      setKbSettings(prev => ({ ...prev, kb_token: data.kb_token, kb_token_created_at: data.kb_token_created_at }));
      toast.success("Token régénéré avec succès");
    } catch (error) {
      toast.error(error.message);
    } finally {
      setRegeneratingToken(false);
    }
  };

  const handleSaveKbPolicies = async () => {
    setSavingPolicies(true);
    try {
      const response = await fetch(`${API}/kb-settings/policies`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(kbPoliciesForm),
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail);
      }
      
      toast.success("Informations enregistrées");
      setShowKbPolicies(false);
      fetchKbSettings();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSavingPolicies(false);
    }
  };

  const addServiceArea = () => {
    if (serviceAreaInput.trim() && !kbPoliciesForm.service_area.includes(serviceAreaInput.trim())) {
      setKbPoliciesForm(prev => ({
        ...prev,
        service_area: [...prev.service_area, serviceAreaInput.trim()]
      }));
      setServiceAreaInput("");
    }
  };

  const removeServiceArea = (zone) => {
    setKbPoliciesForm(prev => ({
      ...prev,
      service_area: prev.service_area.filter(z => z !== zone)
    }));
  };

  const getKbJsonUrl = () => {
    if (!kbSettings?.org_id || !kbSettings?.kb_token) return "";
    return `${FRONTEND_URL}/api/kb/${kbSettings.org_id}?access=${kbSettings.kb_token}`;
  };

  const getKbHtmlUrl = () => {
    if (!kbSettings?.org_id || !kbSettings?.kb_token) return "";
    return `${FRONTEND_URL}/api/kb/${kbSettings.org_id}/html?access=${kbSettings.kb_token}`;
  };

  const handleSavePriorityInstructions = async () => {
    setSavingPriority(true);
    try {
      const response = await fetch(`${API}/kb-settings/priority-instructions`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          title: priorityForm.title,
          content: priorityForm.content,
          start_date: priorityForm.start_date ? new Date(priorityForm.start_date).toISOString() : null,
          end_date: priorityForm.end_date ? new Date(priorityForm.end_date).toISOString() : null,
          is_absolute: priorityForm.is_absolute,
          is_active: priorityForm.is_active,
        }),
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail);
      }
      
      toast.success("Instructions prioritaires enregistrées");
      setShowPriorityInstructions(false);
      fetchKbSettings();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSavingPriority(false);
    }
  };

  const handleDeactivatePriority = async () => {
    try {
      const response = await fetch(`${API}/kb-settings/priority-instructions/deactivate`, {
        method: "POST",
        credentials: "include",
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail);
      }
      
      toast.success("Instructions prioritaires désactivées");
      fetchKbSettings();
    } catch (error) {
      toast.error(error.message);
    }
  };

  // ========== CATALOG IMAGES FOR AI ==========
  const fetchCatalogImages = async () => {
    setLoadingCatalogImages(true);
    try {
      const response = await fetch(`${API}/kb-settings/catalog-images`, { credentials: "include" });
      if (response.ok) {
        const data = await response.json();
        setCatalogImages(data);
      }
    } catch (error) {
    } finally {
      setLoadingCatalogImages(false);
    }
  };

  const handleUploadCatalogImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      toast.error("Veuillez sélectionner une image");
      return;
    }

    setUploadingCatalogImage(true);
    try {
      const formData = new FormData();
      formData.append("file", file);

      const uploadResponse = await fetch(`${API}/upload/structure-image`, {
        method: "POST",
        credentials: "include",
        body: formData,
      });

      if (!uploadResponse.ok) {
        const error = await uploadResponse.json();
        throw new Error(error.detail);
      }

      const uploadResult = await uploadResponse.json();
      setNewCatalogImage(prev => ({ ...prev, image_url: uploadResult.url }));
      toast.success("Image uploadée");
    } catch (error) {
      toast.error(error.message);
    } finally {
      setUploadingCatalogImage(false);
      if (catalogImageInputRef.current) {
        catalogImageInputRef.current.value = "";
      }
    }
  };

  const handleSaveCatalogImage = async () => {
    if (!newCatalogImage.image_url) {
      toast.error("Veuillez uploader une image");
      return;
    }
    if (!newCatalogImage.description.trim()) {
      toast.error("Veuillez ajouter une description pour l'IA");
      return;
    }

    try {
      const response = await fetch(`${API}/kb-settings/catalog-images`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(newCatalogImage),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail);
      }

      toast.success("Image ajoutée au catalogue IA");
      setShowAddCatalogImage(false);
      setNewCatalogImage({ image_url: "", description: "" });
      fetchCatalogImages();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleUpdateCatalogImage = async () => {
    if (!editingCatalogImage) return;

    try {
      const response = await fetch(`${API}/kb-settings/catalog-images/${editingCatalogImage.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ description: editingCatalogImage.description }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail);
      }

      toast.success("Description mise à jour");
      setEditingCatalogImage(null);
      fetchCatalogImages();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleDeleteCatalogImage = async (imageId) => {
    if (!window.confirm("Supprimer cette image du catalogue IA ?")) return;

    try {
      const response = await fetch(`${API}/kb-settings/catalog-images/${imageId}`, {
        method: "DELETE",
        credentials: "include",
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail);
      }

      toast.success("Image supprimée");
      fetchCatalogImages();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const getImageUrl = (url) => {
    if (!url) return "";
    if (url.startsWith("http")) return url;
    return `${process.env.REACT_APP_BACKEND_URL}${url}`;
  };

  const getPriorityStatusBadge = () => {
    if (!kbSettings?.priority_instructions) return null;
    const pi = kbSettings.priority_instructions;
    
    if (!pi.is_active) {
      return <span className="text-xs px-2 py-1 rounded-full bg-gray-100 text-gray-600">Inactive</span>;
    }
    
    switch (pi.status) {
      case "active":
        return (
          <span className="text-xs px-2 py-1 rounded-full bg-green-100 text-green-700 flex items-center gap-1">
            <Zap className="w-3 h-3" />
            Active {pi.days_remaining !== null && `(${pi.days_remaining}j restants)`}
          </span>
        );
      case "scheduled":
        return (
          <span className="text-xs px-2 py-1 rounded-full bg-blue-100 text-blue-700 flex items-center gap-1">
            <Clock className="w-3 h-3" />
            Programmée (dans {pi.days_remaining}j)
          </span>
        );
      case "expired":
        return (
          <span className="text-xs px-2 py-1 rounded-full bg-red-100 text-red-700 flex items-center gap-1">
            <AlertTriangle className="w-3 h-3" />
            Expirée
          </span>
        );
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen festive-gradient confetti-pattern page-container">
      <Header title="Paramètres" />
      
      <main className="p-4 space-y-4 max-w-2xl mx-auto">
        {/* Organization Settings */}
        <Card className="card-festive">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 font-heading">
              <Building2 className="w-5 h-5 text-festive-pink" />
              Informations de l'organisation
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="name">Nom de l'entreprise</Label>
              <Input
                id="name"
                data-testid="org-name-input"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="input-festive mt-1"
                disabled={!isAdmin}
              />
            </div>

            <div>
              <Label htmlFor="phone" className="flex items-center gap-2">
                <Phone className="w-4 h-4 text-gray-400" />
                Téléphone
              </Label>
              <Input
                id="phone"
                type="tel"
                data-testid="org-phone-input"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                className="input-festive mt-1"
                disabled={!isAdmin}
              />
            </div>

            <div>
              <Label htmlFor="email" className="flex items-center gap-2">
                <Mail className="w-4 h-4 text-gray-400" />
                Email de contact
              </Label>
              <Input
                id="email"
                type="email"
                data-testid="org-email-input"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="input-festive mt-1"
                disabled={!isAdmin}
              />
            </div>

            <div>
              <Label htmlFor="address" className="flex items-center gap-2">
                <MapPin className="w-4 h-4 text-gray-400" />
                Adresse
              </Label>
              <Textarea
                id="address"
                data-testid="org-address-input"
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                className="input-festive mt-1"
                disabled={!isAdmin}
                rows={2}
              />
            </div>

            {isAdmin && (
              <Button
                onClick={handleSave}
                disabled={loading}
                data-testid="save-org-settings-btn"
                className="w-full h-12 bg-gradient-to-r from-festive-pink to-festive-purple hover:opacity-90 text-white rounded-xl"
              >
                {loading ? (
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <>
                    <Save className="w-5 h-5 mr-2" />
                    Enregistrer les modifications
                  </>
                )}
              </Button>
            )}

            {!isAdmin && (
              <p className="text-sm text-gray-500 text-center py-2">
                Seuls les administrateurs peuvent modifier ces paramètres
              </p>
            )}
          </CardContent>
        </Card>

        {/* API Keys Management */}
        {isAdmin && (
          <Card className="card-festive">
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span className="flex items-center gap-2 font-heading">
                  <Key className="w-5 h-5 text-festive-purple" />
                  Clés API (Chatbot)
                </span>
                <Button
                  size="sm"
                  onClick={() => setShowCreateKey(true)}
                  data-testid="create-api-key-btn"
                  className="bg-festive-purple hover:bg-festive-purple/90"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Nouvelle clé
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {loadingKeys ? (
                <div className="text-center py-4">
                  <div className="w-6 h-6 border-2 border-festive-pink border-t-transparent rounded-full animate-spin mx-auto" />
                </div>
              ) : apiKeys.length === 0 ? (
                <p className="text-sm text-gray-500 text-center py-4">
                  Aucune clé API. Créez-en une pour permettre à votre chatbot d'accéder aux données.
                </p>
              ) : (
                apiKeys.map((key) => (
                  <div
                    key={key.id}
                    data-testid={`api-key-${key.id}`}
                    className={`p-4 rounded-xl border-2 ${
                      key.is_active ? "border-green-200 bg-green-50" : "border-gray-200 bg-gray-50"
                    }`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div>
                        <p className="font-semibold">{key.name}</p>
                        <p className="text-xs text-gray-500 font-mono">{key.key_prefix}...</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          size="icon"
                          variant="ghost"
                          onClick={() => handleToggleApiKey(key.id)}
                          data-testid={`toggle-key-${key.id}`}
                          className={key.is_active ? "text-green-600" : "text-gray-400"}
                        >
                          <Power className="w-4 h-4" />
                        </Button>
                        <Button
                          size="icon"
                          variant="ghost"
                          onClick={() => handleDeleteApiKey(key.id)}
                          data-testid={`delete-key-${key.id}`}
                          className="text-red-500 hover:text-red-700"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                    <div className="flex items-center justify-between text-xs">
                      <span className={key.is_active ? "text-green-600" : "text-gray-500"}>
                        {key.is_active ? "Active" : "Désactivée"}
                      </span>
                      <span className="text-gray-500">
                        {key.calls_this_month || 0} / {key.monthly_limit} appels ce mois
                      </span>
                    </div>
                  </div>
                ))
              )}

              <div className="mt-4 p-3 rounded-lg bg-amber-50 border border-amber-200">
                <p className="text-xs text-amber-800">
                  <strong>Documentation API :</strong> Utilisez l&apos;en-tête <code className="bg-amber-100 px-1 rounded">X-API-Key</code> pour authentifier vos requêtes.
                  <br />
                  Endpoints disponibles : <code>/api/public/structures</code>, <code>/api/public/offers</code>, <code>/api/public/availability</code>, <code>/api/public/reservations</code>
                </p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Public Booking Page */}
        {isAdmin && (
          <Card className="card-festive">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 font-heading">
                <Globe className="w-5 h-5 text-festive-cyan" />
                Page de réservation publique
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-gray-600">
                Créez une page publique pour permettre à vos clients de réserver directement en ligne.
              </p>

              <div>
                <Label htmlFor="public-slug">Adresse personnalisée (slug)</Label>
                <div className="flex items-center gap-2 mt-1">
                  <span className="text-sm text-gray-500">{FRONTEND_URL}/r/</span>
                  <Input
                    id="public-slug"
                    value={publicSlug}
                    onChange={(e) => setPublicSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-_]/g, ""))}
                    className="input-festive flex-1"
                    placeholder="mon-entreprise"
                    data-testid="public-slug-input"
                  />
                </div>
                <p className="text-xs text-gray-400 mt-1">
                  Lettres minuscules, chiffres, tirets et underscores uniquement
                </p>
              </div>

              <div className="flex items-center justify-between p-4 rounded-xl border-2 border-gray-100">
                <div>
                  <p className="font-medium">Activer la page publique</p>
                  <p className="text-sm text-gray-500">
                    Permet aux clients de réserver directement
                  </p>
                </div>
                <Switch
                  checked={publicBookingEnabled}
                  onCheckedChange={setPublicBookingEnabled}
                  data-testid="public-booking-toggle"
                />
              </div>

              {publicSlug && publicBookingEnabled && (
                <div className="p-4 rounded-xl bg-green-50 border border-green-200">
                  <p className="text-sm text-green-800 mb-2">
                    <strong>Votre page est accessible à :</strong>
                  </p>
                  <div className="flex items-center gap-2">
                    <code className="text-sm bg-green-100 px-2 py-1 rounded flex-1 overflow-x-auto">
                      {FRONTEND_URL}/r/{publicSlug}
                    </code>
                    <Button
                      size="icon"
                      variant="outline"
                      onClick={() => copyToClipboard(`${FRONTEND_URL}/r/${publicSlug}`)}
                      className="shrink-0"
                    >
                      <Copy className="w-4 h-4" />
                    </Button>
                    <Button
                      size="icon"
                      variant="outline"
                      onClick={() => window.open(`${FRONTEND_URL}/r/${publicSlug}`, "_blank")}
                      className="shrink-0"
                    >
                      <ExternalLink className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              )}

              <Button
                onClick={handleSavePublicBooking}
                disabled={savingPublic}
                data-testid="save-public-booking-btn"
                className="w-full h-12 bg-gradient-to-r from-festive-cyan to-festive-purple hover:opacity-90 text-white rounded-xl"
              >
                {savingPublic ? (
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <>
                    <Save className="w-5 h-5 mr-2" />
                    Enregistrer
                  </>
                )}
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Time Slots Configuration */}
        {isAdmin && (
          <Card className="card-festive border-2 border-orange-200">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 font-heading">
                <Clock className="w-5 h-5 text-orange-500" />
                Créneaux horaires
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-gray-600">
                Configurez les créneaux de réservation disponibles pour vos clients.
              </p>

              {loadingTimeSlots ? (
                <div className="text-center py-4">
                  <div className="w-6 h-6 border-2 border-orange-500 border-t-transparent rounded-full animate-spin mx-auto" />
                </div>
              ) : timeSlots ? (
                <div className="space-y-3">
                  {["matin", "apres-midi", "journee"].map((slotId) => {
                    const slot = timeSlots[slotId];
                    if (!slot) return null;
                    return (
                      <div
                        key={slotId}
                        className={`p-4 rounded-xl border-2 transition-colors ${
                          slot.enabled ? "bg-orange-50 border-orange-200" : "bg-gray-50 border-gray-200 opacity-60"
                        }`}
                      >
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-2">
                            <Switch
                              checked={slot.enabled}
                              onCheckedChange={(checked) => updateTimeSlot(slotId, "enabled", checked)}
                              data-testid={`timeslot-toggle-${slotId}`}
                            />
                            <span className={`font-medium ${slot.enabled ? "text-orange-800" : "text-gray-500"}`}>
                              {slotId === "matin" && "🌅 Matin"}
                              {slotId === "apres-midi" && "☀️ Après-midi"}
                              {slotId === "journee" && "📅 Journée complète"}
                            </span>
                          </div>
                        </div>
                        {slot.enabled && (
                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <Label className="text-xs text-gray-500">Libellé</Label>
                              <Input
                                value={slot.name}
                                onChange={(e) => updateTimeSlot(slotId, "name", e.target.value)}
                                className="mt-1 h-9 text-sm"
                                placeholder="Ex: Matin"
                                data-testid={`timeslot-name-${slotId}`}
                              />
                            </div>
                            <div>
                              <Label className="text-xs text-gray-500">Horaires affichés</Label>
                              <Input
                                value={slot.hours}
                                onChange={(e) => updateTimeSlot(slotId, "hours", e.target.value)}
                                className="mt-1 h-9 text-sm"
                                placeholder="Ex: 8h - 13h"
                                data-testid={`timeslot-hours-${slotId}`}
                              />
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}

                  <Button
                    onClick={handleSaveTimeSlots}
                    disabled={savingTimeSlots}
                    className="w-full h-12 bg-gradient-to-r from-orange-500 to-orange-600 hover:opacity-90 text-white rounded-xl"
                    data-testid="save-timeslots-btn"
                  >
                    {savingTimeSlots ? (
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <>
                        <Save className="w-5 h-5 mr-2" />
                        Enregistrer les créneaux
                      </>
                    )}
                  </Button>
                </div>
              ) : (
                <p className="text-sm text-gray-500 text-center py-4">
                  Impossible de charger la configuration des créneaux.
                </p>
              )}
            </CardContent>
          </Card>
        )}

        {/* Knowledge Base IA */}
        {isAdmin && (
          <Card className="card-festive border-2 border-cyan-200">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 font-heading">
                <Brain className="w-5 h-5 text-cyan-500" />
                Base de Connaissances IA
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <p className="text-sm text-gray-600">
                  Exposez automatiquement vos données (structures, tarifs, conditions) à vos agents IA externes (ChatGPT, Claude, Make, N8N...).
                </p>
                <a 
                  href="/documentation" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-cyan-50 text-cyan-700 rounded-lg text-sm font-medium hover:bg-cyan-100 transition-colors shrink-0 ml-4"
                  data-testid="api-docs-link"
                >
                  <Book className="w-4 h-4" />
                  Documentation API
                </a>
              </div>

              {loadingKb ? (
                <div className="text-center py-4">
                  <div className="w-6 h-6 border-2 border-cyan-500 border-t-transparent rounded-full animate-spin mx-auto" />
                </div>
              ) : kbSettings ? (
                <>
                  {/* JSON Endpoint */}
                  <div className="p-4 rounded-xl bg-slate-50 border border-slate-200">
                    <div className="flex items-center gap-2 mb-2">
                      <FileJson className="w-4 h-4 text-cyan-500" />
                      <span className="font-medium text-sm">Endpoint JSON (API)</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <code className="text-xs bg-slate-100 px-2 py-1.5 rounded flex-1 overflow-x-auto font-mono">
                        {getKbJsonUrl()}
                      </code>
                      <Button
                        size="icon"
                        variant="outline"
                        onClick={() => copyToClipboard(getKbJsonUrl())}
                        className="shrink-0"
                        data-testid="copy-kb-json-url"
                      >
                        <Copy className="w-4 h-4" />
                      </Button>
                      <Button
                        size="icon"
                        variant="outline"
                        onClick={() => window.open(getKbJsonUrl(), "_blank")}
                        className="shrink-0"
                      >
                        <ExternalLink className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>

                  {/* HTML Endpoint */}
                  <div className="p-4 rounded-xl bg-slate-50 border border-slate-200">
                    <div className="flex items-center gap-2 mb-2">
                      <FileText className="w-4 h-4 text-cyan-500" />
                      <span className="font-medium text-sm">Endpoint HTML (Scraping)</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <code className="text-xs bg-slate-100 px-2 py-1.5 rounded flex-1 overflow-x-auto font-mono">
                        {getKbHtmlUrl()}
                      </code>
                      <Button
                        size="icon"
                        variant="outline"
                        onClick={() => copyToClipboard(getKbHtmlUrl())}
                        className="shrink-0"
                        data-testid="copy-kb-html-url"
                      >
                        <Copy className="w-4 h-4" />
                      </Button>
                      <Button
                        size="icon"
                        variant="outline"
                        onClick={() => window.open(getKbHtmlUrl(), "_blank")}
                        className="shrink-0"
                      >
                        <ExternalLink className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>

                  {/* Token Info */}
                  <div className="p-4 rounded-xl bg-amber-50 border border-amber-200">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Shield className="w-4 h-4 text-amber-600" />
                        <span className="text-sm font-medium text-amber-800">Token d'accès</span>
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={handleRegenerateKbToken}
                        disabled={regeneratingToken}
                        className="text-amber-700 border-amber-300 hover:bg-amber-100"
                        data-testid="regenerate-kb-token"
                      >
                        {regeneratingToken ? (
                          <div className="w-4 h-4 border-2 border-amber-600 border-t-transparent rounded-full animate-spin" />
                        ) : (
                          <>
                            <RefreshCw className="w-4 h-4 mr-1" />
                            Régénérer
                          </>
                        )}
                      </Button>
                    </div>
                    <p className="text-xs text-amber-700 mt-2">
                      Token créé le {kbSettings.kb_token_created_at ? new Date(kbSettings.kb_token_created_at).toLocaleDateString("fr-FR") : "N/A"}
                      {" • "}{kbSettings.access_logs_count || 0} accès enregistrés
                    </p>
                  </div>

                  {/* Priority Instructions Section */}
                  <div className="p-4 rounded-xl bg-orange-50 border-2 border-orange-200">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <AlertTriangle className="w-5 h-5 text-orange-600" />
                        <span className="font-semibold text-orange-900">Instructions IA prioritaires</span>
                      </div>
                      {getPriorityStatusBadge()}
                    </div>
                    
                    <p className="text-sm text-orange-800 mb-3">
                      Injectez des instructions temporaires ou urgentes qui seront traitées en priorité par vos agents IA.
                    </p>
                    
                    {kbSettings?.priority_instructions?.is_active && kbSettings?.priority_instructions?.status === "active" && (
                      <div className="p-3 bg-white rounded-lg border border-orange-200 mb-3">
                        <p className="text-sm font-medium text-gray-800">
                          {kbSettings.priority_instructions.title || "Instructions actives"}
                        </p>
                        <p className="text-xs text-gray-500 mt-1 line-clamp-2">
                          {kbSettings.priority_instructions.content?.slice(0, 100)}...
                        </p>
                      </div>
                    )}
                    
                    <div className="flex gap-2">
                      <Button
                        onClick={() => setShowPriorityInstructions(true)}
                        className="flex-1 bg-orange-500 hover:bg-orange-600 text-white"
                        data-testid="edit-priority-instructions"
                      >
                        <Zap className="w-4 h-4 mr-2" />
                        {kbSettings?.priority_instructions?.is_active ? "Modifier" : "Créer des instructions"}
                      </Button>
                      {kbSettings?.priority_instructions?.is_active && (
                        <Button
                          onClick={handleDeactivatePriority}
                          variant="outline"
                          className="border-orange-300 text-orange-700 hover:bg-orange-100"
                          data-testid="deactivate-priority"
                        >
                          <Power className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </div>

                  {/* Edit Policies Button */}
                  <Button
                    onClick={() => setShowKbPolicies(true)}
                    variant="outline"
                    className="w-full"
                    data-testid="edit-kb-policies"
                  >
                    <FileText className="w-4 h-4 mr-2" />
                    Modifier les informations exposées
                  </Button>

                  {/* ========== CATALOG IMAGES FOR AI ========== */}
                  <div className="p-4 rounded-xl bg-purple-50 border border-purple-200">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <ImageIcon className="w-4 h-4 text-purple-600" />
                        <span className="font-medium text-sm text-purple-800">Catalogue Images IA</span>
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setShowAddCatalogImage(true)}
                        className="text-purple-600 border-purple-300 hover:bg-purple-100"
                        data-testid="add-catalog-image-btn"
                      >
                        <Plus className="w-4 h-4 mr-1" />
                        Ajouter
                      </Button>
                    </div>
                    <p className="text-xs text-purple-700 mb-3">
                      Uploadez des images avec descriptions pour que votre chatbot IA puisse les envoyer selon le contexte de la conversation.
                    </p>
                    
                    {loadingCatalogImages ? (
                      <div className="text-center py-4">
                        <div className="w-5 h-5 border-2 border-purple-500 border-t-transparent rounded-full animate-spin mx-auto" />
                      </div>
                    ) : catalogImages.length > 0 ? (
                      <div className="space-y-2 max-h-64 overflow-y-auto">
                        {catalogImages.map((img) => (
                          <div key={img.id} className="flex items-start gap-3 p-2 rounded-lg bg-white border border-purple-100">
                            <div className="w-16 h-16 rounded-lg overflow-hidden flex-shrink-0 bg-gray-100">
                              <img
                                src={getImageUrl(img.image_url)}
                                alt="Catalogue"
                                className="w-full h-full object-cover"
                                onError={(e) => { e.target.style.display = 'none'; }}
                              />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-xs text-gray-700 line-clamp-2">{img.description}</p>
                              <code className="text-[10px] text-gray-400 truncate block mt-1">{img.image_url}</code>
                            </div>
                            <div className="flex gap-1 flex-shrink-0">
                              <Button
                                size="icon"
                                variant="ghost"
                                onClick={() => setEditingCatalogImage(img)}
                                className="w-7 h-7 text-gray-500 hover:text-purple-600"
                              >
                                <Pencil className="w-3.5 h-3.5" />
                              </Button>
                              <Button
                                size="icon"
                                variant="ghost"
                                onClick={() => handleDeleteCatalogImage(img.id)}
                                className="w-7 h-7 text-gray-500 hover:text-red-500"
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-xs text-purple-600 text-center py-4">
                        Aucune image dans le catalogue. Ajoutez-en pour enrichir votre chatbot.
                      </p>
                    )}
                    
                    {catalogImages.length > 0 && (
                      <p className="text-xs text-purple-600 mt-2 text-center">
                        {catalogImages.length} image{catalogImages.length > 1 ? 's' : ''} dans la base de connaissances
                      </p>
                    )}
                  </div>

                  {/* Compatibility Badge */}
                  <div className="flex flex-wrap gap-2 pt-2">
                    <span className="text-xs px-2 py-1 rounded-full bg-green-100 text-green-700 flex items-center gap-1">
                      <CheckCircle className="w-3 h-3" /> ChatGPT
                    </span>
                    <span className="text-xs px-2 py-1 rounded-full bg-purple-100 text-purple-700 flex items-center gap-1">
                      <CheckCircle className="w-3 h-3" /> Claude
                    </span>
                    <span className="text-xs px-2 py-1 rounded-full bg-blue-100 text-blue-700 flex items-center gap-1">
                      <CheckCircle className="w-3 h-3" /> Make
                    </span>
                    <span className="text-xs px-2 py-1 rounded-full bg-orange-100 text-orange-700 flex items-center gap-1">
                      <CheckCircle className="w-3 h-3" /> N8N
                    </span>
                  </div>
                </>
              ) : (
                <p className="text-sm text-gray-500 text-center py-4">
                  Impossible de charger les paramètres de la base IA.
                </p>
              )}
            </CardContent>
          </Card>
        )}

        {/* Switch Organization */}
        {organizations?.length > 1 && (
          <Card className="card-festive">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 font-heading">
                <Building className="w-5 h-5 text-festive-purple" />
                Changer d'organisation
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {organizations.map((org) => (
                <button
                  key={org.id}
                  onClick={() => org.id !== organization?.id && handleSwitchOrg(org.id)}
                  disabled={org.id === organization?.id}
                  data-testid={`switch-org-${org.id}`}
                  className={`w-full p-4 rounded-xl border-2 text-left flex items-center gap-3 transition-colors ${
                    org.id === organization?.id
                      ? "border-festive-pink bg-pink-50 cursor-default"
                      : "border-gray-100 hover:border-festive-pink hover:bg-pink-50"
                  }`}
                >
                  <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-festive-pink/20 to-festive-purple/20 flex items-center justify-center">
                    <Building2 className="w-5 h-5 text-festive-pink" />
                  </div>
                  <div className="flex-1">
                    <p className="font-semibold">{org.name}</p>
                    <p className="text-xs text-gray-500 capitalize">{org.role}</p>
                  </div>
                  {org.id === organization?.id ? (
                    <span className="text-xs px-2 py-1 rounded-full bg-festive-pink text-white">
                      Actif
                    </span>
                  ) : (
                    <ArrowRight className="w-5 h-5 text-gray-400" />
                  )}
                </button>
              ))}
            </CardContent>
          </Card>
        )}

        {/* Info */}
        <Card className="card-festive bg-gradient-to-br from-festive-pink/10 to-festive-purple/10">
          <CardContent className="p-4">
            <p className="text-sm text-gray-600">
              <strong>ID Organisation:</strong> {organization?.id}
            </p>
          </CardContent>
        </Card>
      </main>

      {/* Create API Key Dialog */}
      <Dialog open={showCreateKey} onOpenChange={setShowCreateKey}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Créer une clé API</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="keyName">Nom de la clé</Label>
              <Input
                id="keyName"
                data-testid="api-key-name-input"
                value={newKeyName}
                onChange={(e) => setNewKeyName(e.target.value)}
                placeholder="Ex: Chatbot Messenger"
                className="mt-1"
              />
            </div>
            <Button
              onClick={handleCreateApiKey}
              disabled={creatingKey}
              data-testid="confirm-create-key-btn"
              className="w-full bg-festive-purple hover:bg-festive-purple/90"
            >
              {creatingKey ? (
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                "Créer la clé"
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Newly Created Key Dialog */}
      <Dialog open={!!newlyCreatedKey} onOpenChange={() => setNewlyCreatedKey(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-green-600">Clé API créée !</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <p className="text-sm text-amber-800 font-medium mb-2">
                Copiez cette clé maintenant ! Elle ne sera plus affichée.
              </p>
              <div className="flex items-center gap-2">
                <Input
                  type={showKey ? "text" : "password"}
                  value={newlyCreatedKey?.key || ""}
                  readOnly
                  className="font-mono text-sm"
                />
                <Button
                  size="icon"
                  variant="outline"
                  onClick={() => setShowKey(!showKey)}
                >
                  {showKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </Button>
                <Button
                  size="icon"
                  variant="outline"
                  onClick={() => copyToClipboard(newlyCreatedKey?.key)}
                  data-testid="copy-api-key-btn"
                >
                  <Copy className="w-4 h-4" />
                </Button>
              </div>
            </div>
            <Button
              onClick={() => setNewlyCreatedKey(null)}
              className="w-full"
            >
              J'ai copié ma clé
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* KB Policies Dialog */}
      <Dialog open={showKbPolicies} onOpenChange={setShowKbPolicies}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Brain className="w-5 h-5 text-cyan-500" />
              Informations de la Base IA
            </DialogTitle>
            <DialogDescription>
              Ces informations seront exposées aux agents IA pour enrichir leurs réponses.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="kb-description">Description de l'activité</Label>
              <Textarea
                id="kb-description"
                value={kbPoliciesForm.company_description}
                onChange={(e) => setKbPoliciesForm({ ...kbPoliciesForm, company_description: e.target.value })}
                placeholder="Ex: Location de structures gonflables pour événements à La Réunion..."
                className="mt-1"
                rows={3}
              />
            </div>

            <div>
              <Label>Zones desservies</Label>
              <div className="flex gap-2 mt-1">
                <Input
                  value={serviceAreaInput}
                  onChange={(e) => setServiceAreaInput(e.target.value)}
                  placeholder="Ex: Saint-Denis, Saint-Pierre..."
                  onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addServiceArea())}
                />
                <Button type="button" onClick={addServiceArea} variant="outline">
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
              <div className="flex flex-wrap gap-2 mt-2">
                {kbPoliciesForm.service_area.map((zone) => (
                  <span
                    key={zone}
                    className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-cyan-100 text-cyan-700 text-sm"
                  >
                    {zone}
                    <button onClick={() => removeServiceArea(zone)} className="hover:text-cyan-900">
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
            </div>

            <div>
              <Label htmlFor="kb-delivery">Conditions de livraison</Label>
              <Textarea
                id="kb-delivery"
                value={kbPoliciesForm.delivery_conditions}
                onChange={(e) => setKbPoliciesForm({ ...kbPoliciesForm, delivery_conditions: e.target.value })}
                placeholder="Ex: Livraison et installation incluses dans un rayon de 20km..."
                className="mt-1"
                rows={2}
              />
            </div>

            <div>
              <Label htmlFor="kb-cancellation">Politique d'annulation</Label>
              <Textarea
                id="kb-cancellation"
                value={kbPoliciesForm.cancellation_policy}
                onChange={(e) => setKbPoliciesForm({ ...kbPoliciesForm, cancellation_policy: e.target.value })}
                placeholder="Ex: Annulation gratuite jusqu'à 7 jours avant l'événement..."
                className="mt-1"
                rows={2}
              />
            </div>

            <div>
              <Label htmlFor="kb-deposit">Acompte requis</Label>
              <Textarea
                id="kb-deposit"
                value={kbPoliciesForm.deposit_required}
                onChange={(e) => setKbPoliciesForm({ ...kbPoliciesForm, deposit_required: e.target.value })}
                placeholder="Ex: 30% d'acompte à la réservation, solde le jour de l'événement..."
                className="mt-1"
                rows={2}
              />
            </div>

            <div>
              <Label htmlFor="kb-safety">Règles de sécurité</Label>
              <Textarea
                id="kb-safety"
                value={kbPoliciesForm.safety_rules}
                onChange={(e) => setKbPoliciesForm({ ...kbPoliciesForm, safety_rules: e.target.value })}
                placeholder="Ex: Surveillance adulte obligatoire, max 6 enfants simultanément..."
                className="mt-1"
                rows={2}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowKbPolicies(false)}>
              Annuler
            </Button>
            <Button
              onClick={handleSaveKbPolicies}
              disabled={savingPolicies}
              className="bg-gradient-to-r from-cyan-500 to-blue-600"
            >
              {savingPolicies ? (
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Enregistrer
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Priority Instructions Dialog */}
      <Dialog open={showPriorityInstructions} onOpenChange={setShowPriorityInstructions}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-orange-500" />
              Instructions IA prioritaires
            </DialogTitle>
            <DialogDescription>
              Ces instructions seront injectées en PRIORITÉ dans la base de connaissances et interprétées en premier par vos agents IA.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="priority-title">Titre (optionnel)</Label>
              <Input
                id="priority-title"
                value={priorityForm.title}
                onChange={(e) => setPriorityForm({ ...priorityForm, title: e.target.value })}
                placeholder="Ex: Offre spéciale juin, Fermeture exceptionnelle..."
                className="mt-1"
              />
            </div>

            <div>
              <Label htmlFor="priority-content">
                Instructions <span className="text-red-500">*</span>
                <span className="text-xs text-gray-400 ml-2">({priorityForm.content.length}/5000 caractères)</span>
              </Label>
              <Textarea
                id="priority-content"
                value={priorityForm.content}
                onChange={(e) => setPriorityForm({ ...priorityForm, content: e.target.value.slice(0, 5000) })}
                placeholder="Ex: Pour toutes les demandes en juin, proposer en priorité le pack SUMMER PARTY avec -10%. Mentionner que les réservations pour les week-ends de juillet sont presque complètes..."
                className="mt-1 font-mono text-sm"
                rows={8}
              />
              <p className="text-xs text-gray-500 mt-1">
                Supports: Instructions temporaires, promotions, indisponibilités, événements spéciaux, consignes de réponse...
              </p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="priority-start">Date de début</Label>
                <Input
                  id="priority-start"
                  type="date"
                  value={priorityForm.start_date}
                  onChange={(e) => setPriorityForm({ ...priorityForm, start_date: e.target.value })}
                  className="mt-1"
                  disabled={priorityForm.is_absolute}
                />
              </div>
              <div>
                <Label htmlFor="priority-end">Date de fin</Label>
                <Input
                  id="priority-end"
                  type="date"
                  value={priorityForm.end_date}
                  onChange={(e) => setPriorityForm({ ...priorityForm, end_date: e.target.value })}
                  className="mt-1"
                  disabled={priorityForm.is_absolute}
                />
              </div>
            </div>

            <div className="flex items-center justify-between p-4 rounded-xl border-2 border-orange-200 bg-orange-50">
              <div>
                <p className="font-medium text-orange-900">Priorité absolue</p>
                <p className="text-sm text-orange-700">
                  Ignorer les dates, toujours afficher ces instructions
                </p>
              </div>
              <Switch
                checked={priorityForm.is_absolute}
                onCheckedChange={(checked) => setPriorityForm({ ...priorityForm, is_absolute: checked })}
              />
            </div>

            <div className="flex items-center justify-between p-4 rounded-xl border-2 border-green-200 bg-green-50">
              <div>
                <p className="font-medium text-green-900">Activer ces instructions</p>
                <p className="text-sm text-green-700">
                  Les rendre visibles dans la base de connaissances
                </p>
              </div>
              <Switch
                checked={priorityForm.is_active}
                onCheckedChange={(checked) => setPriorityForm({ ...priorityForm, is_active: checked })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPriorityInstructions(false)}>
              Annuler
            </Button>
            <Button
              onClick={handleSavePriorityInstructions}
              disabled={savingPriority || !priorityForm.content}
              className="bg-gradient-to-r from-orange-500 to-red-500"
            >
              {savingPriority ? (
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <>
                  <Zap className="w-4 h-4 mr-2" />
                  Enregistrer
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Catalog Image Modal */}
      <Dialog open={showAddCatalogImage} onOpenChange={setShowAddCatalogImage}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ImageIcon className="w-5 h-5 text-purple-500" />
              Ajouter une image au catalogue IA
            </DialogTitle>
            <DialogDescription>
              Cette image sera disponible dans la base de connaissances pour votre chatbot IA.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Image Upload */}
            <div className="space-y-2">
              <Label>Image</Label>
              <input
                ref={catalogImageInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                onChange={handleUploadCatalogImage}
                className="hidden"
                id="catalog-image-upload"
              />
              {newCatalogImage.image_url ? (
                <div className="relative">
                  <img
                    src={getImageUrl(newCatalogImage.image_url)}
                    alt="Preview"
                    className="w-full h-48 object-cover rounded-lg border"
                  />
                  <Button
                    size="icon"
                    variant="secondary"
                    onClick={() => setNewCatalogImage(prev => ({ ...prev, image_url: "" }))}
                    className="absolute top-2 right-2"
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              ) : (
                <label
                  htmlFor="catalog-image-upload"
                  className="flex flex-col items-center justify-center w-full h-48 border-2 border-dashed border-purple-300 rounded-lg cursor-pointer hover:bg-purple-50 transition-colors"
                >
                  {uploadingCatalogImage ? (
                    <div className="w-8 h-8 border-2 border-purple-500 border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <>
                      <Upload className="w-8 h-8 text-purple-400 mb-2" />
                      <span className="text-sm text-purple-600">Cliquez pour uploader</span>
                      <span className="text-xs text-gray-400 mt-1">JPG, PNG, WebP</span>
                    </>
                  )}
                </label>
              )}
            </div>

            {/* Description */}
            <div className="space-y-2">
              <Label htmlFor="catalog-image-desc">Description pour l'IA *</Label>
              <Textarea
                id="catalog-image-desc"
                value={newCatalogImage.description}
                onChange={(e) => setNewCatalogImage(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Décrivez le contexte où cette image doit être envoyée. Ex: 'Photo du château gonflable princesse en situation réelle lors d'un anniversaire'"
                rows={4}
                className="resize-none"
              />
              <p className="text-xs text-gray-500">
                Cette description aide l'IA à comprendre quand utiliser cette image.
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowAddCatalogImage(false);
              setNewCatalogImage({ image_url: "", description: "" });
            }}>
              Annuler
            </Button>
            <Button 
              onClick={handleSaveCatalogImage}
              disabled={!newCatalogImage.image_url || !newCatalogImage.description.trim()}
              className="bg-purple-600 hover:bg-purple-700"
            >
              <Plus className="w-4 h-4 mr-2" />
              Ajouter au catalogue
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Catalog Image Modal */}
      <Dialog open={!!editingCatalogImage} onOpenChange={() => setEditingCatalogImage(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Pencil className="w-5 h-5 text-purple-500" />
              Modifier la description
            </DialogTitle>
          </DialogHeader>
          
          {editingCatalogImage && (
            <div className="space-y-4 py-4">
              <div className="w-full h-48 rounded-lg overflow-hidden bg-gray-100">
                <img
                  src={getImageUrl(editingCatalogImage.image_url)}
                  alt="Catalogue"
                  className="w-full h-full object-cover"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="edit-catalog-desc">Description pour l'IA</Label>
                <Textarea
                  id="edit-catalog-desc"
                  value={editingCatalogImage.description}
                  onChange={(e) => setEditingCatalogImage(prev => ({ ...prev, description: e.target.value }))}
                  rows={4}
                  className="resize-none"
                />
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingCatalogImage(null)}>
              Annuler
            </Button>
            <Button onClick={handleUpdateCatalogImage} className="bg-purple-600 hover:bg-purple-700">
              <Save className="w-4 h-4 mr-2" />
              Enregistrer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <BottomNav />
    </div>
  );
}
